/**
 * `SCR-CLOTHLIST` (5 states), `SCR-CLOTHDET` (4), `SCR-MOVE` (5),
 * `SCR-SWATCHQ` (4). FLOWS_SPEC §10.7–§10.8. OWNER: web-frontend-engineer.
 *
 * Cloth metadata is READ-ONLY in Round 1 and re-seeded from `rules.json`. The
 * screen STATES that rather than showing dead fields — a back office that could
 * edit `weight_gsm` would create two sources of truth for a rule input.
 */

import { useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import { api } from '../../lib/api.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import type { AtelierClothRow, SwatchQueueRow } from '../../lib/contracts.ts';
import { dateInWords, decimal, joinList, metres } from '@pt/view';
import { patternLabel, present, weaveLabel } from '../../lib/label.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import {
  BackOfficeTable,
  SearchField,
  StockLedger,
  StockMovementForm,
  type Column,
} from '../../components/backoffice.tsx';
import { Button, FactLine, RovingRadioGroup, Statement } from '../../components/primitives.tsx';
import { FetchError, LoadingRegion } from '../../components/states.tsx';

/** OPS_SPEC §5.5's bands, as the catalogue's four stock-level words. Text,
 *  never colour alone. The band is the server's; only the key is ours. */
function stockLevelKey(band: string): string {
  if (band === 'out_of_stock' || band === 'discontinued') return 'outOfStock';
  if (band === 'last_length') return 'lastLength';
  if (band === 'limited' || band === 'insufficient_for_this_commission') return 'limited';
  return 'inStock';
}

/* ── THE LIST'S VIEW: a filter, a search, a page ────────────────────────────
 *
 * 5 September 2026, finding C3. /atelier/cloth listed the whole book — 1,798
 * rows, 157,080 px tall (measured at 1280×800 on a static render of the seeded
 * catalogue) — with no search and no page, and thirty of its cells carried a
 * seven-line paragraph. Nobody standing at a cutting table reads a list that
 * long; they need the cloths that need something done about them, and a way to
 * reach any one cloth by name or reference.
 *
 * So the default view is what the atelier needs first — the rows the SERVER
 * marks `needsAttention` (the rank it has always sorted by, plus the low-stock
 * flag of ST-CLOTHLIST-LOWFLAG) — and the whole book is one chip away, fifty to
 * a page, in the server's own order. The search is the Clients screen's, and
 * composes with the chip exactly as the order queue's filters compose, with the
 * same filtered-empty sentence and the same one act to clear them.
 *
 * "The house's own cloths" was asked for as part of that first view. No field
 * in the platform marks a cloth as the house's own — the twenty-four
 * `placeholder` rows are stand-ins, not a selection — so that half is a seam
 * (a third chip, when the flag exists) and not a guess (law 32(a), (d)).
 */

export const CLOTH_PAGE_SIZE = 50;

export type ClothListFilter = 'attention' | 'all';
const CLOTH_FILTERS: readonly ClothListFilter[] = ['attention', 'all'];

export interface ClothListView {
  readonly filter: ClothListFilter;
  readonly search: string;
  /** 1-based. Out of range clamps to the last page rather than showing nothing. */
  readonly page: number;
}

export interface ClothListPage<Row> {
  readonly rows: readonly Row[];
  readonly total: number;
  readonly fromRow: number;
  readonly toRow: number;
  readonly page: number;
  readonly pageCount: number;
}

/** Name or reference, case-insensitively — the same two facts the Clients search reads. */
export function clothMatches(row: Pick<AtelierClothRow, 'name' | 'id'>, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  return row.name.toLowerCase().includes(needle) || row.id.toLowerCase().includes(needle);
}

/** Below the low-stock threshold — the server's threshold, the server's figure. */
export function belowLowStock(row: Pick<AtelierClothRow, 'onHandM' | 'lowStockThresholdM'>): boolean {
  return row.lowStockThresholdM !== undefined && row.onHandM < row.lowStockThresholdM;
}

export function clothListPage<Row extends Pick<AtelierClothRow, 'name' | 'id' | 'needsAttention'>>(
  all: readonly Row[],
  view: ClothListView,
): ClothListPage<Row> {
  const matching = all
    .filter((row) => (view.filter === 'attention' ? row.needsAttention === true : true))
    .filter((row) => clothMatches(row, view.search));
  const pageCount = Math.max(1, Math.ceil(matching.length / CLOTH_PAGE_SIZE));
  const page = Math.min(Math.max(1, Math.trunc(view.page)), pageCount);
  const start = (page - 1) * CLOTH_PAGE_SIZE;
  const rows = matching.slice(start, start + CLOTH_PAGE_SIZE);
  return {
    rows,
    total: matching.length,
    fromRow: rows.length === 0 ? 0 : start + 1,
    toRow: start + rows.length,
    page,
    pageCount,
  };
}

/**
 * The search and the two chips. `attentionKnown` is false for the factory's
 * narrower row, which carries no band and therefore no judgement about
 * attention — the workshop sees the whole book, searched and paged, and no chip
 * that would have to lie about stock.
 */
export function ClothListFilters({
  search,
  onSearch,
  filter,
  onFilter,
  attentionKnown,
}: {
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly filter: ClothListFilter;
  readonly onFilter: (value: ClothListFilter) => void;
  readonly attentionKnown: boolean;
}) {
  return (
    <div className="pt-filters">
      {attentionKnown ? (
        <fieldset className="pt-filter">
          <legend className="pt-filter__legend">{t('clothlist.filter.legend')}</legend>
          <RovingRadioGroup
            ariaLabel={t('clothlist.filter.legend')}
            values={CLOTH_FILTERS}
            chosen={filter}
            labelOf={(value) => tDynamic(`clothlist.filter.${value}`)}
            onChoose={(value) => onFilter(value as ClothListFilter)}
            rowClassName="pt-filter__values pt-filter__values--row"
          />
        </fieldset>
      ) : null}
      <div className="pt-filter">
        <SearchField id="pt-cloth-search" label={t('clothlist.search.label')} value={search} onChange={onSearch} />
      </div>
    </div>
  );
}

/**
 * Where the page stands, and the acts that move it. A page that does not exist
 * has no button — L-C: nothing is disabled, so an act that cannot be taken is
 * simply not offered.
 */
export function ClothListPager<Row>({
  page,
  onPage,
}: {
  readonly page: ClothListPage<Row>;
  readonly onPage: (page: number) => void;
}) {
  if (page.total <= CLOTH_PAGE_SIZE) return null;
  return (
    <div className="pt-actions">
      <FactLine>
        {t('clothlist.page.range', {
          fromRow: String(page.fromRow),
          toRow: String(page.toRow),
          totalRows: String(page.total),
        })}
      </FactLine>
      {page.page > 1 ? (
        <Button onClick={() => onPage(page.page - 1)}>{t('clothlist.page.previous')}</Button>
      ) : null}
      {page.page < page.pageCount ? (
        <Button onClick={() => onPage(page.page + 1)}>{t('clothlist.page.next')}</Button>
      ) : null}
    </div>
  );
}

/**
 * The stock-level cell. The band's word, and — below the threshold — a ONE-LINE
 * flag. The full sentence ("Below the low-stock threshold of 7.2 m. This flag is
 * for the workshop and never reaches a client screen.") stands on the cloth's
 * own page, one row-open away, where there is room to read it (C3).
 */
export function ClothStockLevel({ row }: { row: AtelierClothRow }) {
  return (
    <>
      {tDynamic(`clothlist.stock.${stockLevelKey(row.stockBand ?? '')}`)}
      {belowLowStock(row) ? <span className="pt-table__sub">{t('clothlist.lowflag.short')}</span> : null}
    </>
  );
}

/** The full low-stock sentence, on the cloth's own page; nothing when the row is not below it. */
export function LowStockStatement({ row }: { row: AtelierClothRow }) {
  if (!belowLowStock(row) || row.lowStockThresholdM === undefined) return null;
  return <FactLine>{t('clothlist.lowflag', { thresholdMetres: metres(row.lowStockThresholdM) })}</FactLine>;
}

/** The columns, with or without the three the factory's row does not carry. */
export function clothListColumns(narrowed: boolean): readonly Column<AtelierClothRow>[] {
  return [
    {
      id: 'cloth',
      headingKey: 'clothlist.column.cloth',
      render: (row) => (
        <>
          {row.name}
          <span className="pt-table__sub">{row.id}</span>
        </>
      ),
    },
    {
      id: 'weave',
      headingKey: 'clothlist.column.weave',
      render: (row) => present([weaveLabel(row.weave), patternLabel(row.pattern)]).join(' · '),
    },
    { id: 'weight', headingKey: 'clothlist.column.weight', numeric: true, render: (row) => String(row.weight_gsm) },
    { id: 'width', headingKey: 'clothlist.column.width', numeric: true, render: (row) => String(row.width_cm) },
    /* THREE COLUMNS ARE ABSENT FOR THE FACTORY, not blank. `factoryClothPayload`
       is an allow-list: a price band, the standard suit length and the stock
       band simply are not in the workshop's row. A blank cell under a heading
       is a question; a heading that is not there is an answer. */
    ...(narrowed
      ? []
      : [
          {
            id: 'band',
            headingKey: 'clothlist.column.priceBand',
            render: (row: AtelierClothRow) => row.priceBand ?? '',
          } as Column<AtelierClothRow>,
        ]),
    { id: 'onhand', headingKey: 'clothlist.column.onHand', numeric: true, render: (row) => metres(row.onHandM) },
    { id: 'reserved', headingKey: 'clothlist.column.reserved', numeric: true, render: (row) => metres(row.reservedM) },
    { id: 'available', headingKey: 'clothlist.column.available', numeric: true, render: (row) => metres(row.availableForCommissionM) },
    ...(narrowed
      ? []
      : [
          {
            id: 'longest',
            headingKey: 'clothlist.column.longest',
            numeric: true,
            render: (row: AtelierClothRow) =>
              row.standardSuitLengthM === undefined ? '' : metres(row.standardSuitLengthM),
          } as Column<AtelierClothRow>,
        ]),
    {
      id: 'state',
      headingKey: 'clothlist.column.state',
      render: (row) => tDynamic(`clothlist.state.${row.lifecycleState}`),
    },
    /* THE BAND IS A JUDGEMENT THE FACTORY'S ROW DOES NOT CARRY, and the old
       code's default made that dangerous rather than merely missing:
       `stockLevelKey(undefined)` fell through to "In stock" and said so about
       a bolt with nothing on it. The metres are in the workshop's row and are
       shown three columns to the left; a wrong word about stock is worse than
       no word. */
    ...(narrowed
      ? []
      : [
          {
            id: 'level',
            // Stock level is TEXT, never colour alone.
            headingKey: 'clothlist.column.stockLevel',
            render: (row: AtelierClothRow) => <ClothStockLevel row={row} />,
          } as Column<AtelierClothRow>,
        ]),
  ];
}

export function ClothListScreen() {
  const { navigate } = useNavigation();
  const cloths = useResource(() => api.atelierCloths(), []);
  const [search, setSearchText] = useState('');
  const [filter, setFilterChip] = useState<ClothListFilter>('attention');
  const [pageNumber, setPageNumber] = useState(1);
  const all = cloths.data?.cloths ?? [];
  /* True when the server sent the FACTORY's narrower row. Keyed off the field
     itself rather than the viewer's role: the screen does not hold its own copy
     of who may see what. */
  const narrowed = all.length > 0 && all[0]?.stockBand === undefined;
  const attentionKnown = all.length > 0 && all[0]?.needsAttention !== undefined;
  const withheld = all[0]?.commercialsWithheld ?? null;

  const view: ClothListView = { filter: attentionKnown ? filter : 'all', search, page: pageNumber };
  const page = clothListPage(all, view);
  const columns = clothListColumns(narrowed);

  // A new search or a new chip starts at the first page of what it shows.
  const setSearch = (value: string) => {
    setSearchText(value);
    setPageNumber(1);
  };
  const setFilter = (value: ClothListFilter) => {
    setFilterChip(value);
    setPageNumber(1);
  };

  const filtersOn = view.search.trim() !== '' || view.filter === 'attention';

  return (
    <AppShell title={t('clothlist.heading')}>
      {cloths.phase === 'skeleton' || cloths.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="table" slow={cloths.slow} label={t('clothlist.heading')} />
      ) : null}
      {cloths.phase === 'error' ? <FetchError onRetry={cloths.reload} /> : null}
      {/* The server's own sentence about the columns that are not there. */}
      {withheld === null ? null : <FactLine>{withheld}</FactLine>}
      {cloths.phase !== 'ready' ? null : all.length === 0 ? (
        <Statement>{t('clothlist.empty')}</Statement>
      ) : (
        <>
          <ClothListFilters
            search={search}
            onSearch={setSearch}
            filter={view.filter}
            onFilter={setFilter}
            attentionKnown={attentionKnown}
          />
          {page.total === 0 ? (
            <div>
              {/* Names the filters that are on and clears them in one act — the
                  order queue's ST-QUEUE-FILTERED-EMPTY, on the cloth list. A
                  needs-attention view with nothing in it is good news, and is
                  said as such rather than as "no cloth matches". */}
              <Statement>
                {view.search.trim() === '' && view.filter === 'attention'
                  ? t('clothlist.attention.empty')
                  : t('clothlist.filteredEmpty', {
                      activeFiltersList: joinList(
                        [
                          view.filter === 'attention' ? t('clothlist.filter.attention') : '',
                          view.search.trim() === ''
                            ? ''
                            : t('clothlist.filter.searchTerm', { searchText: view.search.trim() }),
                        ].filter((value) => value !== ''),
                      ),
                    })}
              </Statement>
              {filtersOn ? (
                <div className="pt-actions">
                  <Button
                    onClick={() => {
                      setSearchText('');
                      setFilterChip('all');
                      setPageNumber(1);
                    }}
                  >
                    {t('global.action.clearFilters')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <BackOfficeTable
                caption={t('clothlist.heading')}
                columns={columns}
                rows={page.rows}
                rowKey={(row) => row.id}
                rowHref={(row) => `/atelier/cloth/${row.id}`}
                onOpenRow={(row) => navigate(`/atelier/cloth/${row.id}`)}
              />
              <ClothListPager page={page} onPage={setPageNumber} />
            </>
          )}
        </>
      )}
    </AppShell>
  );
}

export function ClothDetailScreen({ clothId }: { clothId: string }) {
  const cloth = useResource(() => api.atelierCloth(clothId), [clothId]);
  const submission = useSubmission();
  const data = cloth.data;

  return (
    <AppShell title={data?.cloth.name ?? t('clothlist.heading')}>
      {cloth.phase === 'skeleton' || cloth.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="table" slow={cloth.slow} label={t('clothlist.heading')} />
      ) : null}
      {cloth.phase === 'error' ? <FetchError onRetry={cloth.reload} /> : null}

      {data === null ? null : (
        <>
          {/* ST-CLOTHDET-METAREADONLY — stated, not shown as dead fields. */}
          <Statement>{t('clothdet.metaReadonly')}</Statement>

          <dl>
            <FactLine label={t('clothlist.column.onHand')}>{metres(data.cloth.onHandM)}</FactLine>
            <FactLine label={t('clothlist.column.reserved')}>{metres(data.cloth.reservedM)}</FactLine>
            <FactLine label={t('clothlist.column.available')}>{metres(data.cloth.availableForCommissionM)}</FactLine>
            <FactLine label={t('clothlist.column.weight')}>{decimal(data.cloth.weight_gsm, 0)}</FactLine>
            <FactLine label={t('clothlist.column.state')}>
              {tDynamic(`clothlist.state.${data.cloth.lifecycleState}`)}
            </FactLine>
          </dl>

          {/* The low-stock sentence in full — the list carries the one-line
              flag, this page carries what it means (C3, 5 September 2026). */}
          <LowStockStatement row={data.cloth} />

          {data.cloth.lifecycleState === 'discontinued' &&
          data.cloth.discontinuedAt !== null &&
          data.cloth.discontinuedAt !== undefined ? (
            <Statement>
              {t('clothdet.discontinued.statement', {
                discontinuedDate: dateInWords(data.cloth.discontinuedAt),
                reason: data.cloth.discontinuedReason ?? '',
              })}
            </Statement>
          ) : null}

          <div className="pt-actions">
            <Button onClick={() => void api.setClothLifecycle(clothId, { lifecycleState: 'published' })}>
              {t('clothdet.publish')}
            </Button>
            <Button onClick={() => void api.setClothLifecycle(clothId, { lifecycleState: 'unpublished' })}>
              {t('clothdet.unpublish')}
            </Button>
          </div>

          <StockMovementForm
            clothName={data.cloth.name}
            currentOnHandM={data.cloth.onHandM}
            busy={submission.busy}
            refusal={submission.rejection === null ? null : <FetchError whatKey="move.error.means" />}
            onSubmit={(move) => {
              const bolt = data.bolts[0];
              if (bolt === undefined) return;
              void submission
                .run(() =>
                  api.moveStock(clothId, {
                    boltId: bolt.boltId,
                    deltaM: move.deltaM,
                    reason: move.reason as never,
                    note: move.note,
                  }),
                )
                .then((result) => result && cloth.reload());
            }}
          />

          {/* The movement ledger — append-only, NEWEST LAST so the running
              balance reads down the page. This is how "where did four metres
              go" gets answered. */}
          <section className="pt-section">
            <h2 className="pt-cutsheet__blockTitle">{t('clothdet.ledger.heading')}</h2>
            {/* Its own column words (C10, 5 September 2026) — see StockLedger. */}
            <StockLedger ledger={data.ledger} />
          </section>
        </>
      )}
    </AppShell>
  );
}

/**
 * `SCR-SWATCHQ`. A list, oldest first, with one action: Mark sent. That is the
 * whole feature, and it is what makes the client-facing promise true.
 *
 * `ST-SWATCHQ-SENT` CLAIMS NOTHING WAS SENT TO THE CLIENT — no transport is
 * wired, and the copy must never say "you will be notified automatically".
 */
export function SwatchQueueScreen() {
  const queue = useResource(() => api.swatchRequests(), []);
  const submission = useSubmission();
  const requests = [...(queue.data?.requests ?? [])].sort((a, b) =>
    a.requestedAt.localeCompare(b.requestedAt),
  );
  const interests = queue.data?.interests ?? [];

  const columns: readonly Column<SwatchQueueRow>[] = [
    { id: 'client', headingKey: 'swatchq.column.client', render: (row) => row.clientName },
    { id: 'cloth', headingKey: 'swatchq.column.cloth', render: (row) => row.clothName },
    {
      id: 'requested',
      headingKey: 'swatchq.column.requested',
      render: (row) => dateInWords(row.requestedAt),
    },
    {
      id: 'state',
      headingKey: 'swatchq.column.state',
      render: (row) =>
        row.state === 'sent' ? (
          <FactLine>
            {t('swatchq.sent.statement', {
              actorName: row.sentByName ?? '',
              sentAt: row.sentAt === null ? '' : dateInWords(row.sentAt),
            })}
          </FactLine>
        ) : (
          <Button
            busy={submission.busy}
            busyLabel={t('trans.busy')}
            onClick={() => {
              void submission.run(() => api.markSwatchSent(row.swatchRequestId)).then(() => queue.reload());
            }}
          >
            {t('swatchq.markSent')}
          </Button>
        ),
    },
  ];

  return (
    <AppShell title={t('swatchq.heading')}>
      {queue.phase === 'skeleton' || queue.phase === 'idle' ? (
        <LoadingRegion rows={6} variant="table" slow={queue.slow} label={t('swatchq.heading')} />
      ) : null}
      {queue.phase === 'error' ? <FetchError onRetry={queue.reload} /> : null}

      {queue.phase !== 'ready' ? null : requests.length === 0 ? (
        <Statement>{t('swatchq.empty')}</Statement>
      ) : (
        <BackOfficeTable
          caption={t('swatchq.heading')}
          columns={columns}
          rows={requests}
          rowKey={(row) => row.swatchRequestId}
        />
      )}

      <section className="pt-section">
        <h2 className="pt-cutsheet__blockTitle">{t('swatchq.interest.heading')}</h2>
        <Statement>{t('swatchq.interest.note')}</Statement>
        {interests.length === 0 ? (
          <FactLine>{t('swatchq.interest.empty')}</FactLine>
        ) : (
          <ul>
            {interests.map((interest) => (
              <li key={interest.clothInterestId} className="pt-row">
                <FactLine>
                  {t('swatchq.interest.entry', {
                    clientNameStaff: interest.clientName,
                    clothName: interest.clothName,
                    requestedDate: dateInWords(interest.requestedAt),
                  })}
                </FactLine>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppShell>
  );
}
